import { F } from '@/utils/intl';
import { Space } from 'antd';
import React, { useCallback, useMemo, useRef } from 'react';
import FragmentInsertSelect, {
  InsertableFragment,
} from '../../FragmentInsertSelect';
import VariableInsertButton from '../../VariableInsertButton';
import { buildVariableSnippet } from '../insertion';
import TwigCodeEditor, { TwigCodeEditorHandle } from '../TwigCodeEditor';
import { EmailVariableGroup } from '../types';
import { stripEnvelope } from './envelope';
import { prettifyTwigSource } from './prettifySource';

import styles from './SourcePanel.less';

interface SourcePanelProps {
  /** The compiled body, exactly as it would be stored. */
  body: string;
  /** Cmd/Ctrl-click on an `{% include %}` opens that building block. */
  onOpenFragment?: (key: string) => void;
  /**
   * Editing the source. Absent ⇒ read-only.
   *
   * The buffer is exactly what gets stored as the body — the design document
   * lives in its own field (`design_document`), so there is nothing riding
   * inside the body for an edit to corrupt.
   */
  onChange?: (source: string) => void;
  /**
   * Send-time variables offered for insertion while editing.
   *
   * These moved here when the separate code editor was removed (A7): writing
   * `{{ email_config.template_vars.rescheduleURL }}` correctly from memory is not a
   * reasonable thing to ask, and it was the code editor's toolbar that made it
   * unnecessary.
   */
  variableGroups?: EmailVariableGroup[];
  /** The partner's building blocks, insertable as `{% include %}`. */
  insertableFragments?: InsertableFragment[];
  /**
   * An NL2Go body: its placeholders are named differently, so every path we could
   * insert would render empty. The button and the `{{` variable list go; editing
   * the source, and the saved texts on that list, stay.
   */
  variablesUnsupported?: boolean;
}

/**
 * The Twig source of the email — read-only, or the raw editing surface.
 *
 * Since the implementation switch was removed (A7) this **is** the code editor: the
 * only way to edit a legacy body, hand-written Twig, or anything the block model
 * cannot express. It carries the insert tools that used to live in the separate
 * editor's toolbar, and editing it detaches the document (G3).
 *
 * A body saved before the `design_document` column may still carry the legacy
 * `{# cvn:design #}` envelope; it is stripped from display (and from the edit
 * buffer — the backend strips it from storage on the next save anyway).
 *
 * ### Prettifying
 *
 * Read-only only, and display-only: what is stored never changes, and legacy one-line
 * bodies are unreadable without it. While editing it is off — the buffer has to be
 * exactly what gets saved, so re-indenting it would silently rewrite the user's Twig.
 */
const SourcePanel: React.FC<SourcePanelProps> = ({
  body,
  onOpenFragment,
  onChange,
  variableGroups,
  insertableFragments,
  variablesUnsupported,
}) => {
  const editorRef = useRef<TwigCodeEditorHandle>(null);
  /** Nothing to offer, so nothing is offered — see `variablesUnsupported`. */
  const insertableVariables = variablesUnsupported ? undefined : variableGroups;

  /**
   * The editor applies the edit at its live selection and fires `onChange`, so the
   * value stays in sync without being threaded through here.
   */
  const insert = useCallback((snippet: string) => {
    editorRef.current?.insertAtCursor(snippet);
  }, []);

  const handleInsertVariable = useCallback(
    (namespace: string, name: string) => {
      insert(buildVariableSnippet(namespace, name));
    },
    [insert],
  );

  const editable = Boolean(onChange);
  const withoutEnvelope = useMemo(() => stripEnvelope(body), [body]);

  const source = useMemo(
    () => (editable ? withoutEnvelope : prettifyTwigSource(withoutEnvelope)),
    [editable, withoutEnvelope],
  );

  return (
    <div className={styles.sourcePanel}>
      {editable && (
        <div className={styles.bar}>
          {/* Keeps the insert tools on their side of the bar. */}
          <span className={styles.toggles} />

          <Space size={8}>
            {!variablesUnsupported && (
              <VariableInsertButton
                variableGroups={variableGroups}
                onInsert={handleInsertVariable}
                label={
                  <F
                    id="pages.emailCenter.editor.insertVariable"
                    defaultMessage="Insert variable"
                  />
                }
              />
            )}
            <FragmentInsertSelect
              fragments={insertableFragments ?? []}
              onInsert={insert}
            />
          </Space>
        </div>
      )}

      <div className={styles.editor}>
        <TwigCodeEditor
          ref={editorRef}
          value={source}
          readOnly={!editable}
          minHeight="100%"
          maxHeight="100%"
          variableGroups={insertableVariables}
          onChange={onChange}
          onOpenReference={onOpenFragment}
        />
      </div>
    </div>
  );
};

export default React.memo(SourcePanel);
